import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  ImageExtensions,
  OfficeExtensions,
  PdfExtensions,
} from '@qaroni-shared/types/file-extension.enum';
import { FileViewerDialogData } from '@qaroni-shared/types/file-viewer-dialog-data';

@Component({
  selector: 'qaroni-file-viewer-dialog',
  templateUrl: './file-viewer-dialog.component.html',
  styleUrl: './file-viewer-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FileViewerDialogComponent implements OnInit {
  public fileCategory: 'image' | 'pdf' | 'office' | 'unknown' = 'unknown';
  public safeFileUrl: SafeResourceUrl;
  public canZoom = false;
  private zoomLevel = 1;

  public data: FileViewerDialogData = inject(MAT_DIALOG_DATA);
  private sanitizer = inject(DomSanitizer);
  private window = inject(Window);

  ngOnInit(): void {
    this.determineFileCategory();
    if (this.fileCategory === 'pdf') {
      const viewerUrl = this.getSafePdfUrl(this.data.fileUrl);
      this.safeFileUrl =
        this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
    } else {
      this.safeFileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.data.fileUrl
      );
    }
  }

  get title(): string {
    return this.data.fileName ? this.data.fileName : 'Archivo sin nombre';
  }

  get fileUrl(): string {
    return this.data?.fileUrl;
  }

  get fileIcon() {
    if (this.fileCategory === 'image') {
      return 'image';
    } else if (this.fileCategory === 'pdf') {
      return 'picture_as_pdf';
    } else if (this.fileCategory === 'office') {
      return 'description';
    } else {
      return 'insert_drive_file';
    }
  }

  get fileIconColor() {
    if (this.fileCategory === 'image') {
      return 'primary';
    } else if (this.fileCategory === 'pdf') {
      return 'warn';
    } else if (this.fileCategory === 'office') {
      return 'accent';
    } else {
      return '';
    }
  }

  get useViewer(): boolean {
    return this.data?.useViewer ?? true;
  }

  public zoomIn(): void {
    if (this.canZoom) {
      this.zoomLevel = Math.min(this.zoomLevel + 0.25, 3);
      this.applyZoom();
    }
  }

  public zoomOut(): void {
    if (this.canZoom) {
      this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.25);
      this.applyZoom();
    }
  }

  public resetZoom(): void {
    if (this.canZoom) {
      this.zoomLevel = 1;
      this.applyZoom();
    }
  }

  public downloadFile(): void {
    if (!this.window) {
      return;
    }

    this.window.open(this.data.fileUrl, 'download');
  }

  private determineFileCategory(): void {
    const extension = this.fileExtension.toLowerCase();

    if (ImageExtensions.includes(extension)) {
      this.fileCategory = 'image';
      this.canZoom = true;
    } else if (PdfExtensions.includes(extension)) {
      this.fileCategory = 'pdf';
    } else if (OfficeExtensions.includes(extension)) {
      this.fileCategory = 'office';
    } else {
      this.fileCategory = 'unknown';
    }
  }

  private get fileExtension() {
    if (!this.data?.fileName) {
      return '';
    }

    return this.data.fileName.split('.').pop()?.toUpperCase() || '';
  }

  private applyZoom(): void {
    const image = document.querySelector('.file-preview-image') as HTMLElement;
    if (image) {
      image.style.transform = `scale(${this.zoomLevel})`;
    }
  }

  private getSafePdfUrl(fileUrl: string): string {
    const encodedUrl = encodeURIComponent(fileUrl);
    return this.useViewer
      ? `https://docs.google.com/gview?embedded=true&url=${encodedUrl}`
      : fileUrl;
  }
}
